import { type ReactNode, useEffect, useRef } from 'react';
import { useLingui } from '@lingui/react/macro';

import { Button, Flex } from '@rover/kibble/core';

type Props = {
  isManaging: boolean;
  /** Set when the controls sit on a dark background, as they do in the lightbox. */
  contrast?: boolean;
  /** Drawn opposite the mode button, so the host's header shares this row. */
  gridHeader?: ReactNode;
  /** Drawn after the mode button, as the lightbox does with its close button. */
  headerEnd?: ReactNode;
  onEnterManageMode: () => void;
  onCancel: () => void;
};

const ImageGridManageControls = ({
  isManaging,
  contrast = false,
  gridHeader,
  headerEnd,
  onEnterManageMode,
  onCancel,
}: Props): JSX.Element => {
  const { t } = useLingui();
  const manageRef = useRef<HTMLElement>(null);
  const doneRef = useRef<HTMLElement>(null);
  const wasManaging = useRef(isManaging);

  // Switching modes replaces the pressed button, dropping keyboard focus.
  useEffect(() => {
    if (wasManaging.current === isManaging) return;
    wasManaging.current = isManaging;
    (isManaging ? doneRef : manageRef).current?.focus?.();
  }, [isManaging]);

  const modeButton = (button: JSX.Element): JSX.Element => (
    <Flex flexDirection="row" width="100%" flexWrap="wrap" alignItems="center" gap="3x">
      {gridHeader}
      <Flex flexDirection="row" flex="1" justifyContent="flex-end" gap="3x">
        {button}
        {headerEnd}
      </Flex>
    </Flex>
  );

  if (!isManaging) {
    return modeButton(
      <Button size="small" onPress={onEnterManageMode} webProps={{ ref: manageRef }}>
        {t`Manage photos`}
      </Button>
    );
  }

  return modeButton(
    <Button
      variant="flat"
      size="small"
      contrast={contrast}
      onPress={onCancel}
      webProps={{ ref: doneRef }}
    >
      {t`Done`}
    </Button>
  );
};

export default ImageGridManageControls;
